import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DataTableWithFilters,
  type DataTableFilter,
} from "@/components/ui/DataTableWithFilter";
import {
  buildRangeFilter,
  DEFAULT_PAGE_SIZE,
  type DateRangeFilter,
} from "@/helpers";
import {
  callingAgentsService,
  type CallingAgentCallHistory,
  type CallingAgentCallHistoryListRequest,
  type CallingAgentDropdownOption,
} from "@/services/api/callingAgents";
import { useToast } from "@/components/ui";

type SortState = {
  by?: string;
  dir?: "asc" | "desc";
};

type HistoryRow = {
  id: string;
  lead: string;
  phone: string;
  agent: string;
  status: string;
  direction: string;
  duration_seconds: string;
  created_at: string;
  __raw: CallingAgentCallHistory;
};

type CallHistoryFilterState = {
  calling_agent_uuid?: string;
  dateRange?: DateRangeFilter;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const getString = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
};

const readString = (
  source: Record<string, unknown> | null | undefined,
  key: string,
): string | undefined => (source ? getString(source[key]) : undefined);

const titleCase = (value?: string | null) => {
  const normalized = value
    ?.replace(/PhoneCall$/i, "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .trim();

  if (!normalized) return "-";

  return normalized
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDuration = (value?: number | string | null) => {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds <= 0) return "-";

  const totalSeconds = Math.round(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;

  if (minutes === 0) return `${remainingSeconds}s`;

  return `${minutes}m ${String(remainingSeconds).padStart(2, "0")}s`;
};

const getLeadName = (history: CallingAgentCallHistory) => {
  const metadataLead = isRecord(history.metadata?.lead)
    ? history.metadata.lead
    : undefined;

  return (
    getString(history.contact_name) ??
    readString(history.lead_details, "fullName") ??
    readString(metadataLead, "fullName") ??
    "-"
  );
};

const getPhone = (history: CallingAgentCallHistory) => {
  const metadataLead = isRecord(history.metadata?.lead)
    ? history.metadata.lead
    : undefined;
  const leadPhone = readString(history.lead_details, "phoneNumber");
  const leadPhoneCode = readString(history.lead_details, "phoneCode");

  return (
    getString(history.phone) ??
    readString(history.contact_details, "number") ??
    (leadPhoneCode && leadPhone ? `${leadPhoneCode}${leadPhone}` : undefined) ??
    readString(metadataLead, "phoneNumber") ??
    "-"
  );
};

const getDropdownOptions = (response: any): CallingAgentDropdownOption[] => {
  const options = response?.attributes ?? response?.data ?? [];

  return Array.isArray(options) ? options : [];
};

const statusClassName = (status?: string | null) => {
  switch (status?.toLowerCase()) {
    case "ended":
    case "completed":
      return "bg-green-100 text-green-800";
    case "queued":
    case "ringing":
    case "in-progress":
    case "in_progress":
      return "bg-blue-100 text-blue-800";
    case "failed":
    case "error":
    case "cancelled":
    case "canceled":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

const HistoryLogs = () => {
  const { error: toastError } = useToast();
  const [histories, setHistories] = useState<CallingAgentCallHistory[]>([]);
  const [agentOptions, setAgentOptions] = useState<CallingAgentDropdownOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAgentsLoading, setIsAgentsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState("");
  const [activeFilters, setActiveFilters] = useState<CallHistoryFilterState>({});
  const [sortState, setSortState] = useState<SortState>({
    by: "created_at",
    dir: "desc",
  });

  const handleSearch = useCallback((query: string) => {
    setSearch(query);
    setCurrentPage(1);
  }, []);

  const handleItemsPerPageChange = useCallback((nextItemsPerPage: number) => {
    setItemsPerPage(nextItemsPerPage);
    setCurrentPage(1);
  }, []);

  const handleSort = useCallback((column: string, direction: "asc" | "desc") => {
    setSortState({ by: column, dir: direction });
    setCurrentPage(1);
  }, []);

  const handleFilterChange = useCallback((filters: Record<string, unknown>) => {
    setActiveFilters((prev) => {
      const next: CallHistoryFilterState = { ...prev };

      if ("calling_agent_uuid" in filters) {
        next.calling_agent_uuid = getString(filters.calling_agent_uuid);
      }

      if ("dateRange" in filters) {
        next.dateRange = isRecord(filters.dateRange)
          ? (filters.dateRange as unknown as DateRangeFilter)
          : undefined;
      }

      return next;
    });
    setCurrentPage(1);
  }, []);

  const requestFilters = useMemo<
    NonNullable<CallingAgentCallHistoryListRequest["filters"]>
  >(() => {
    const filters: NonNullable<CallingAgentCallHistoryListRequest["filters"]> =
      {};

    if (activeFilters.calling_agent_uuid) {
      filters.calling_agent_uuid = activeFilters.calling_agent_uuid;
    }

    Object.assign(filters, buildRangeFilter(activeFilters.dateRange));

    return filters;
  }, [activeFilters]);

  const fetchCallHistories = useCallback(async () => {
    setIsLoading(true);

    try {
      const res = await callingAgentsService.callHistoryListing({
        page: currentPage,
        page_size: itemsPerPage,
        filters: requestFilters,
        search: search || undefined,
        sort: sortState.by
          ? { [sortState.by]: sortState.dir ?? "desc" }
          : { created_at: "desc" },
      });

      const list: CallingAgentCallHistory[] =
        res?.attributes?.callHistories ??
        res?.attributes?.data ??
        res?.data ??
        [];
      const total: number = res?.attributes?.pagination?.total ?? list.length;

      setHistories(list);
      setTotalItems(total);
    } catch (err: any) {
      toastError(err?.message || "Failed to load call history");
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, itemsPerPage, requestFilters, search, sortState, toastError]);

  useEffect(() => {
    fetchCallHistories();
  }, [fetchCallHistories]);

  useEffect(() => {
    let isMounted = true;

    const fetchAgents = async () => {
      setIsAgentsLoading(true);

      try {
        const res = await callingAgentsService.dropdown();
        const list = getDropdownOptions(res);

        if (isMounted) {
          setAgentOptions(list);
        }
      } catch (err: any) {
        if (isMounted) {
          toastError(err?.message || "Failed to load agent options");
        }
      } finally {
        if (isMounted) {
          setIsAgentsLoading(false);
        }
      }
    };

    fetchAgents();

    return () => {
      isMounted = false;
    };
  }, [toastError]);

  const tableFilters = useMemo<DataTableFilter[]>(
    () => [
      {
        key: "calling_agent_uuid",
        label: "Agent",
        type: "select",
        placeholder: isAgentsLoading ? "Loading agents..." : "All Agents",
        options: agentOptions,
      },
    ],
    [agentOptions, isAgentsLoading],
  );

  const rows = useMemo<HistoryRow[]>(
    () =>
      histories.map((history) => ({
        id: history.uuid,
        lead: getLeadName(history),
        phone: getPhone(history),
        agent:
          getString(history.callingAgent?.display_name) ??
          getString(history.assistant_id) ??
          "-",
        status: history.status ?? "-",
        direction: titleCase(history.direction),
        duration_seconds: formatDuration(history.duration_seconds),
        created_at: formatDateTime(history.started_at ?? history.created_at),
        __raw: history,
      })),
    [histories],
  );

  const columns = [
    {
      label: "Lead",
      key: "lead",
      render: (value: string, row: HistoryRow) => (
        <div className="min-w-40">
          <p className="font-medium text-gray-900">{value}</p>
          {row.__raw.contact_email && (
            <p className="text-xs text-gray-500">{row.__raw.contact_email}</p>
          )}
        </div>
      ),
    },
    { label: "Phone", key: "phone" },
    { label: "Agent", key: "agent" },
    {
      label: "Status",
      key: "status",
      render: (value: string) => (
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${statusClassName(value)}`}
        >
          {titleCase(value)}
        </span>
      ),
    },
    { label: "Direction", key: "direction" },
    { label: "Duration", key: "duration_seconds" },
    { label: "Called At", key: "created_at", sortable: true },
  ];

  const renderHistoryDetails = (row: HistoryRow) => {
    const history = row.__raw;
    const recordingUrl =
      getString(history.recording_url) ??
      getString(history.stereo_recording_url);

    return (
      <div className="grid gap-4 text-sm md:grid-cols-2">
        <div>
          <p className="text-xs font-semibold uppercase text-gray-400">
            Summary
          </p>
          <p className="mt-1 whitespace-pre-wrap text-gray-700">
            {history.summary || "-"}
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase text-gray-400">
              Ended Reason
            </p>
            <p className="mt-1 text-gray-700">
              {titleCase(history.ended_reason)}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase text-gray-400">
              Ended At
            </p>
            <p className="mt-1 text-gray-700">
              {formatDateTime(history.ended_at)}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase text-gray-400">
              Lead ID
            </p>
            <p className="mt-1 text-gray-700">{history.lead_id ?? "-"}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase text-gray-400">
              Campaign ID
            </p>
            <p className="mt-1 text-gray-700">{history.campaign_id ?? "-"}</p>
          </div>
        </div>
        {history.transcript && (
          <div className="md:col-span-2">
            <p className="text-xs font-semibold uppercase text-gray-400">
              Transcript
            </p>
            <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap rounded-md bg-white p-3 text-xs text-gray-700 ring-1 ring-gray-200">
              {history.transcript}
            </pre>
          </div>
        )}
        {recordingUrl && (
          <div className="md:col-span-2">
            <p className="text-xs font-semibold uppercase text-gray-400">
              Recording
            </p>
            <audio className="mt-2 w-full" controls src={recordingUrl}>
              <a href={recordingUrl} target="_blank" rel="noreferrer">
                Open recording
              </a>
            </audio>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">
            History Logs Management
          </h1>
        </div>
      </div>
      <DataTableWithFilters
        title="History Logs"
        columns={columns}
        data={rows}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={handleItemsPerPageChange}
        filters={tableFilters}
        showFilters
        showDateRangeFilter
        showExport={false}
        loading={isLoading}
        onSearch={handleSearch}
        onFilterChange={handleFilterChange}
        onSort={handleSort}
        searchDebounceMs={500}
        searchPlaceholder="Search phone, lead, email, or call ID"
        expandedRowRender={renderHistoryDetails}
      />
    </div>
  );
};

export default HistoryLogs;
